import {
  createPostMessage,
  HandshakeAckRejectedError,
  type PostMessageClient,
} from "@connect/post-message/client";
import { sendToParent, type ConnectMessageMap } from "@connect/post-message/protocol";
import { initializeStore } from "@connect/credentials-storage";
import { log } from "@connect/observability";
import { resolveCredentialsEnvironment } from "../../lib/parse-frame-config.ts";
import { stampFramesVersion } from "../../lib/frames-version.ts";
import { enableMocking } from "../../lib/enable-mocking.ts";
import { setupObservability } from "../../lib/observability-setup.ts";
import { trackWidgetVisited } from "../../lib/use-widget-visited.ts";
import { renderConfigError } from "../../utils/render-config-error.ts";
import { reportFrameInitFailure } from "../../utils/report-frame-init-failure.ts";
import { parseConfig, validateConfig } from "./config.ts";
import { checkConnection } from "./check-connection.ts";
import { createExternalMessages } from "./external-messages.ts";

stampFramesVersion();

const params = new URLSearchParams(window.location.search);

initializeStore(resolveCredentialsEnvironment());

// Wire early so error/rejection handlers emit. Session token is the only auth available here.
const earlySessionToken = params.get("sessionToken") || undefined;
setupObservability({
  frame: "check-connection",
  getSessionToken: () => earlySessionToken,
});

const earlyChannelId = params.get("channelId") ?? "unknown";

try {
  await enableMocking();
} catch (err) {
  // non-fatal — continue without mocks, but surface the failure
  log.warn("enableMocking failed", {
    reason: err instanceof Error ? err.message : String(err),
  });
}

let config;
try {
  config = parseConfig();
} catch (err) {
  // Route parse errors to both postMessage and observability.
  await reportFrameInitFailure(earlyChannelId, err);
  renderConfigError();
}

if (config) {
  trackWidgetVisited("check-connection");

  const configErrors = validateConfig(config);
  if (configErrors.length > 0) {
    // A broken config can also break the handshake itself, so this must not
    // depend on an ack ever arriving -- send directly, not via a pm.send that
    // would buffer it behind a handshake that may never complete.
    const sendDirect: PostMessageClient<ConnectMessageMap>["send"] = (kind, ...args) =>
      sendToParent(config.channelId, kind, args[0]);
    await createExternalMessages(sendDirect).validationError(configErrors);
    renderConfigError();
  } else {
    try {
      const pm = createPostMessage<ConnectMessageMap>({ channelId: config.channelId });
      await pm.connected;
      await checkConnection(config, pm.send);
    } catch (err) {
      // A rejected ack already sent its own specific error to the parent
      // (see listenForParent's origin check) -- log it, but don't send the
      // generic fallback too and duplicate the signal.
      if (err instanceof HandshakeAckRejectedError) {
        log.error(err);
      } else {
        await reportFrameInitFailure(config.channelId, err);
      }
    }
  }
}
